import type { AuditEntry, HubUser, UsersResponse } from "@central-hub/contracts";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./api";
import type { Session } from "./auth";
import { dayClock } from "./format";
import { Icon } from "./icons";
import { LEVEL_LABEL, userRows } from "./model";

const LEVELS: { id: HubUser["level"]; help: string }[] = [
  { id: "read", help: "Sees their sites: status, history, deploys, errors." },
  { id: "deploy", help: "Also gets the Deploy link and rollback commands of their sites." },
  { id: "admin", help: "Everything, on every site, inviting others included." },
];

interface Draft {
  email: string;
  level: HubUser["level"];
  all: boolean;
  sites: string[];
  infra: boolean;
  /** Editing an existing user: the email can't change. */
  editing: boolean;
}

const EMPTY: Draft = {
  email: "",
  level: "read",
  all: false,
  sites: [],
  infra: false,
  editing: false,
};

/**
 * Invited users (admins only): who may sign in, at which level and on which sites. They sign
 * in with Google under the invited email; nothing is sent to them.
 */
export function UsersView({ session, now }: { session: Session; now: number }) {
  const [data, setData] = useState<UsersResponse | null>(null);
  const [activity, setActivity] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const call = useCallback(
    async <T,>(run: (token: string) => Promise<T>): Promise<T | undefined> => {
      const token = await session.getToken();
      if (!token) {
        setError("Your session expired: sign in again.");
        return undefined;
      }
      try {
        setError(null);
        return await run(token);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "The hub didn’t answer.");
        return undefined;
      }
    },
    [session],
  );

  const load = useCallback(async () => {
    const [result, audit] = await Promise.all([
      call((t) => api.users(t)),
      call((t) => api.audit(t, 50)),
    ]);
    if (result) setData(result);
    if (audit) setActivity(audit.entries);
  }, [call]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const email = draft.email.trim().toLowerCase();
    if (!draft.all && draft.level !== "admin" && draft.sites.length === 0) {
      setError("Pick at least one site, or all sites.");
      return;
    }
    setBusy(true);
    const saved = await call((t) =>
      api.saveUser(t, email, {
        level: draft.level,
        sites: draft.all || draft.level === "admin" ? null : draft.sites,
        infra: draft.level !== "admin" && draft.infra,
      }),
    );
    setBusy(false);
    if (!saved) return;
    setNotice(
      draft.editing
        ? `${email} updated.`
        : `${email} invited: they sign in at ${window.location.origin} with Google, using this email.`,
    );
    setDraft(EMPTY);
    await load();
  };

  const remove = async (email: string) => {
    if (!window.confirm(`Remove ${email}? They lose access at their next request.`)) return;
    const done = await call((t) => api.removeUser(t, email));
    if (done === undefined) return;
    setNotice(`${email} removed.`);
    if (draft.email === email) setDraft(EMPTY);
    await load();
  };

  const edit = (user: HubUser) => {
    setNotice(null);
    setDraft({
      email: user.email,
      level: user.level,
      all: user.sites === null,
      sites: user.sites ?? [],
      infra: user.infra,
      editing: true,
    });
  };

  const toggleSite = (site: string) =>
    setDraft((d) => ({
      ...d,
      sites: d.sites.includes(site) ? d.sites.filter((s) => s !== site) : [...d.sites, site],
    }));

  const rows = data ? userRows(data.users, now) : [];
  const sites = data?.sites ?? [];

  return (
    <main className="view">
      <section className="panel" aria-labelledby="invite-title">
        <div className="panel-head">
          <h2 id="invite-title" className="eyebrow">
            {draft.editing ? `Edit ${draft.email}` : "Invite someone"}
          </h2>
          <span className="num">They sign in with Google under this email</span>
        </div>
        <form className="invite" onSubmit={submit}>
          <label className="invite-email">
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="off"
              placeholder="name@example.com"
              value={draft.email}
              disabled={draft.editing}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </label>

          <fieldset className="invite-levels">
            <legend>Access level</legend>
            {LEVELS.map((l) => (
              <label key={l.id} className="choice" data-checked={draft.level === l.id}>
                <input
                  type="radio"
                  name="level"
                  value={l.id}
                  checked={draft.level === l.id}
                  onChange={() => setDraft({ ...draft, level: l.id })}
                />
                <b>{LEVEL_LABEL[l.id]}</b>
                <span>{l.help}</span>
              </label>
            ))}
          </fieldset>

          {draft.level !== "admin" && (
            <fieldset className="invite-sites">
              <legend>Sites</legend>
              <label className="choice-inline invite-all">
                <input
                  type="checkbox"
                  checked={draft.all}
                  onChange={(e) => setDraft({ ...draft, all: e.target.checked })}
                />
                All sites, including ones added later
              </label>
              {!draft.all && (
                <div className="invite-site-grid">
                  {sites.map((site) => (
                    <label key={site} className="choice-inline">
                      <input
                        type="checkbox"
                        checked={draft.sites.includes(site)}
                        onChange={() => toggleSite(site)}
                      />
                      {site}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}

          {draft.level !== "admin" && (
            <fieldset className="invite-extra">
              <legend>Also</legend>
              <label className="choice-inline">
                <input
                  type="checkbox"
                  checked={draft.infra}
                  onChange={(e) => setDraft({ ...draft, infra: e.target.checked })}
                />
                Cluster and Security views, showing only their sites
              </label>
            </fieldset>
          )}

          {error && (
            <p className="notice" data-status="down" role="alert">
              <Icon name="down" />
              <span>{error}</span>
            </p>
          )}
          {notice && !error && (
            <p className="notice" data-status="up" role="status">
              <Icon name="up" />
              <span>{notice}</span>
            </p>
          )}

          <div className="invite-actions">
            <button type="submit" className="primary" disabled={busy}>
              {draft.editing ? "Save changes" : "Invite"}
            </button>
            {draft.editing && (
              <button type="button" className="linkbtn" onClick={() => setDraft(EMPTY)}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="panel" aria-labelledby="users-title">
        <div className="panel-head">
          <h2 id="users-title" className="eyebrow">
            Users
          </h2>
          <span className="num">
            {data ? `${rows.length} invited` : "Loading…"} · admins from Auth0 roles aren’t listed
          </span>
        </div>
        {data && rows.length === 0 && <p className="muted">Nobody invited yet.</p>}
        <ul className="users">
          {rows.map((row) => {
            const user = data?.users.find((u) => u.email === row.email);
            return (
              <li key={row.email} className="user-row">
                <span className="user-email">{row.email}</span>
                <span className="tag" data-level={row.level}>
                  {row.levelLabel}
                </span>
                <span className="user-sites">
                  {row.sites}
                  {row.infra && <span className="muted"> · + Cluster &amp; Security</span>}
                </span>
                <span className="muted user-meta">
                  {row.lastSeen} · invited {row.invited}
                </span>
                <span className="user-actions">
                  <button type="button" className="linkbtn" onClick={() => user && edit(user)}>
                    Edit
                  </button>
                  <button type="button" className="linkbtn" onClick={() => remove(row.email)}>
                    Remove
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="panel" aria-labelledby="activity-title">
        <div className="panel-head">
          <h2 id="activity-title" className="eyebrow">
            Activity
          </h2>
          <span className="num">who changed users, read logs or held alerts · last 50</span>
        </div>
        {activity && activity.length === 0 && <p className="muted">Nothing recorded yet.</p>}
        <ol className="activity">
          {activity?.map((e) => (
            <li key={e.id} className="activity-row">
              <span className="num muted">{dayClock(e.at)}</span>
              <span className="activity-actor">{e.actor}</span>
              <span>
                {ACTION_LABEL[e.action] ?? e.action}
                {e.target && <b> {e.target}</b>}
                {e.detail && <span className="muted"> {auditDetail(e.detail)}</span>}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}

const ACTION_LABEL: Record<string, string> = {
  "user.invite": "invited",
  "user.update": "changed",
  "user.remove": "removed",
  "maintenance.start": "held alerts of",
  "maintenance.end": "ended the maintenance of",
  "kubernetes.log.read": "read the log of",
  "notification.test": "sent a test notification",
};

/** The few details worth a glance: level and sites, the reason, which pod. */
function auditDetail(detail: Record<string, unknown>): string {
  const parts = [
    detail.level && `as ${String(detail.level)}`,
    detail.sites &&
      `on ${Array.isArray(detail.sites) ? detail.sites.join(", ") : String(detail.sites)}`,
    detail.reason && `“${String(detail.reason)}”`,
    detail.pod && `(${String(detail.pod)})`,
  ].filter(Boolean);
  return parts.join(" ");
}
